import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { GetArgoCDContainerLogsResponse, GetContainerLogsResponse, KubernetesLiveStatusDetailedResource, KubernetesPodLiveStatusDetailedResource, Repository } from "@octopusdeploy/octopus-server-client";
import moment, { type Moment } from "moment/moment";
import { useCallback, useState } from "react";
import { delay } from "~/utils/delay";
type UseContainerLogsQueryReturnValue = InlineStatusQuery<GetContainerLogsResponse | GetArgoCDContainerLogsResponse | undefined | null, never> & {
    requestCompletedTime: Moment;
};
export type ContainerLogFilters = {
    search?: string;
    podId?: string;
    containerName?: string;
};
export function useContainerLogsQuery(detailedResource: KubernetesLiveStatusDetailedResource | undefined, projectId: string, environmentId: string, tenantId: string | undefined, allPodResources: Record<string, KubernetesPodLiveStatusDetailedResource>, containerName: string | undefined, podId: string | undefined, showPreviousContainer: boolean): UseContainerLogsQueryReturnValue {
    const [requestCompletedTime, setRequestCompletedTime] = useState<Moment>(moment());
    const kubernetesMonitorQuery = useCallback(async (repository: Repository, abortSignal: AbortSignal, detailedResource: KubernetesLiveStatusDetailedResource, podName: string, podId: string, containerName: string) => {
        const { SessionId } = await repository.KubernetesLiveStatus.beginContainerLogsSession(projectId, environmentId, tenantId, detailedResource.ResourceSourceId, podId, podName, containerName, showPreviousContainer);
        //while this is not aborted, continue attempting to load
        while (!abortSignal.aborted) {
            const result = await repository.KubernetesLiveStatus.getContainerLogs(SessionId);
            if (result.IsSessionCompleted) {
                setRequestCompletedTime(moment());
                return result;
            }
            await delay(100, abortSignal);
        }
    }, [projectId, environmentId, tenantId, showPreviousContainer]);
    const argoCDQuery = useCallback(async (repository: Repository, detailedResource: KubernetesLiveStatusDetailedResource, podName: string, podId: string, containerName: string) => {
        const id = detailedResource.ResourceId;
        if (!id) {
            return Promise.reject("The live resource does not have a ResourceId");
        }
        const response = await repository.KubernetesLiveStatus.getArgoCDContainerLogs(projectId, environmentId, tenantId, detailedResource.ResourceSourceId, podId, podName, containerName, showPreviousContainer);
        setRequestCompletedTime(moment());
        return response;
    }, [environmentId, projectId, showPreviousContainer, tenantId]);
    const result = useInlineStatusQuery(async (repo, abortSignal) => {
        if (!detailedResource || detailedResource.SourceType === "ArgoCDInstance" || !containerName || !podId) {
            return null;
        }
        const podName = allPodResources[podId].Name;
        if (!podName) {
            return null;
        }
        if (detailedResource.SourceType === "KubernetesMonitor") {
            return await kubernetesMonitorQuery(repo, abortSignal, detailedResource, podName, podId, containerName);
        }
        else {
            return await argoCDQuery(repo, detailedResource, podName, podId, containerName);
        }
    }, [allPodResources, argoCDQuery, containerName, detailedResource, kubernetesMonitorQuery, podId], "Load container logs for live object");
    return {
        ...result,
        requestCompletedTime,
    };
}
